//
// Imports
//

import * as BrowserUtilities from "@donutteam/browser-utilities";
import { createNotice } from "@donutteam/document-builder-components/build/client.js";
import { DateTime } from "luxon";

import { ResponseBodySchema as CreateUserAvatarResponseBodySchema } from "../../../../api/routes/v6/user-avatars/create.schemas.js";

import { RequestBody as DeleteUserAvatarRequestBody, ResponseBodySchema as DeleteUserAvatarResponseBodySchema } from "../../../../api/routes/v6/user-avatars/delete.schemas.js";

//
// Component
//

export function initialiseAvatarForm(avatarForm: HTMLFormElement)
{
	const apiUrl = BrowserUtilities.ElementClientLib.getStringDataOrThrow(avatarForm, "apiUrl");

	const noticeContainer = BrowserUtilities.ElementClientLib.getElementOrThrow(avatarForm, ".notice-container");

	const avatarWrapper = BrowserUtilities.ElementClientLib.getElementOrThrow(avatarForm, ".avatar-wrapper");

	let avatar = BrowserUtilities.ElementClientLib.getElementOrThrow<HTMLImageElement>(avatarWrapper, ".avatar");

	const uploadOverlay = BrowserUtilities.ElementClientLib.getElementOrThrow(avatarWrapper, ".upload-overlay");

	const deleteButton = BrowserUtilities.ElementClientLib.getElementOrThrow<HTMLButtonElement>(avatarForm, `[data-action="delete"]`);

	const uploadButton = BrowserUtilities.ElementClientLib.getElementOrThrow<HTMLButtonElement>(avatarForm, `[data-action="upload"]`);

	const reloadAvatar = async () =>
	{
		const clonedAvatar = avatar.cloneNode(true) as HTMLImageElement;

		const url = new URL(clonedAvatar.src);

		url.searchParams.set("time", DateTime.utc().toSeconds().toString());

		clonedAvatar.src = url.toString();

		await new Promise(
			(resolve) =>
			{
				clonedAvatar.addEventListener("load", resolve, { once: true });
			});

		avatar.replaceWith(clonedAvatar);
	};

	avatarForm.addEventListener("submit",
		(event) =>
		{
			event.preventDefault();
		});

	deleteButton.addEventListener("click",
		async () =>
		{
			noticeContainer.replaceChildren();

			try
			{
				avatarWrapper.setAttribute("data-loading", "true");

				deleteButton.disabled = true;
				uploadButton.disabled = true;

				const user_id = BrowserUtilities.ElementClientLib.getIntegerDataOrThrow(avatarForm, "user");
	
				const userAvatar_id = BrowserUtilities.ElementClientLib.getIntegerDataOrThrow(avatarForm, "userAvatar");
	
				const requestBody: DeleteUserAvatarRequestBody =
				{
					user_id,
					userAvatar_id,
				};
	
				const response = await fetch(apiUrl + "/v6/user-avatars/delete",
					{
						credentials: "include",
						method: "POST",
						headers:
						{
							"Content-Type": "application/json",
						},
						body: JSON.stringify(requestBody),
					});
	
				const responseBody = DeleteUserAvatarResponseBodySchema.parse(await response.json());
	
				if (!responseBody.success)
				{
					const notices = responseBody.errors.map((error) => createNotice(
						{
							type: "warning",
							message: error.message,
							dismissible: true,
						}));

					return noticeContainer.replaceChildren(...notices);
				}
	
				await reloadAvatar();

				avatarForm.dataset["usingUploadedAvatar"] = "false";

				noticeContainer.replaceChildren(createNotice(
					{
						type: "success",
						message: "Avatar deleted successfully. Note that this change may take a little while to be reflected across the site.",
						dismissible: true,
					}));
			}
			catch (error)
			{
				deleteButton.disabled = false;

				noticeContainer.replaceChildren(createNotice(
					{
						type: "danger",
						message: error instanceof Error ? error.message : "Something went wrong.",
						dismissible: true,
					}));
			}
			finally
			{
				avatarWrapper.setAttribute("data-loading", "false");

				uploadButton.disabled = false;
			}
		});

	uploadButton.addEventListener("click",
		async () =>
		{
			try
			{
				noticeContainer.replaceChildren();

				deleteButton.disabled = true;
				uploadButton.disabled = true;

				const fileInput = document.createElement("input");

				fileInput.type = "file";
	
				fileInput.accept = "image/*";

				fileInput.click();

				await new Promise<void>(
					(resolve) =>
					{
						fileInput.addEventListener("cancel",
							async () =>
							{
								console.log("[AvatarForm] Cancelled");

								resolve();
							});

						fileInput.addEventListener("change",
							async () =>
							{
								console.log("[AvatarForm] Changed");

								resolve();
							});
					});

				const avatar = fileInput.files != null ? fileInput.files[0] : null;

				if (avatar == null)
				{
					return noticeContainer.replaceChildren(createNotice(
						{
							type: "warning",
							message: "No file selected.",
							dismissible: true,
						}));
				}

				if (avatar.size > 1024 * 1024 * 5)
				{
					return noticeContainer.replaceChildren(createNotice(
						{
							type: "warning",
							message: "The file you selected is too large. Please select a file that is 5 MB or smaller.",
							dismissible: true,
						}));
				}

				avatarWrapper.setAttribute("data-loading", "true");

				const formData = new FormData();

				formData.set("avatar", avatar, avatar.name);

				const response = await fetch(apiUrl + "/v6/user-avatars/create",
					{
						credentials: "include",
						method: "POST",
						body: formData,
					});

				const responseBody = CreateUserAvatarResponseBodySchema.parse(await response.json());

				if (!responseBody.success)
				{
					const notices = responseBody.errors.map((error) => createNotice(
						{
							type: "warning",
							message: error.message,
							dismissible: true,
						}));

					return noticeContainer.replaceChildren(...notices);
				}

				await reloadAvatar();

				avatarForm.dataset["userAvatar"] = responseBody.userAvatar.id.toString();
				avatarForm.dataset["usingUploadedAvatar"] = "true";

				noticeContainer.replaceChildren(createNotice(
					{
						type: "success",
						message: "Avatar updated successfully. Note that this change may take a little while to be reflected across the site.",
						dismissible: true,
					}));
			}
			catch (error)
			{
				noticeContainer.replaceChildren(createNotice(
					{
						type: "danger",
						message: error instanceof Error ? error.message : "Something went wrong.",
						dismissible: true,
					}));
			}
			finally
			{
				avatarWrapper.setAttribute("data-loading", "false");

				deleteButton.disabled = false;
				uploadButton.disabled = false;
			}
		});

	uploadOverlay.addEventListener("click",
		() =>
		{
			uploadButton.click();
		});

	avatarForm.classList.add("initialised");
}

export function initialiseAvatarForms()
{
	const avatarForms = document.querySelectorAll<HTMLFormElement>(".component-avatar-form:not(.initialised)");

	console.log("[AvatarForm] Initialising " + avatarForms.length + " instances...");

	for (const avatarForm of avatarForms)
	{
		try
		{
			initialiseAvatarForm(avatarForm);
		}
		catch (error)
		{
			console.error("[AvatarForm] Error initialising:", avatarForm, error);
		}
	}
}